import { Button, ButtonProps, Empty, Flex, Upload, UploadFile, UploadProps } from 'antd';
import { forwardRef, Fragment, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { downloadFileUrl } from '@/apis/file';
import { DeleteOutlined, DownloadOutlined, InboxOutlined, UploadOutlined } from '@ant-design/icons';

import IconWord from '@/assets/images/fileIcon/word.svg';
import IconExcel from '@/assets/images/fileIcon/excel.svg';
import IconPpt from '@/assets/images/fileIcon/ppt.svg';
import IconPdf from '@/assets/images/fileIcon/pdf.svg';
import IconTxt from '@/assets/images/fileIcon/txt.svg';
import IconZip from '@/assets/images/fileIcon/zip.svg';
import IconAudio from '@/assets/images/fileIcon/audio.svg';
import IconVideo from '@/assets/images/fileIcon/video.svg';
import IconImage from '@/assets/images/fileIcon/image.svg';
import IconUnknown from '@/assets/images/fileIcon/unknown.svg';
import { useTranslation } from 'react-i18next';
import { downloadFile } from '../SDoc/utils';
import { WFile } from '@/types/file';

const IMAGE_TYPES = ['bmp', 'gif', 'jpe', 'jpeg', 'jpg', 'ico', 'png', 'apng', 'webp'];

const WORD_TYPES = ['doc', 'docx', 'docm', 'dot'];
const EXCEL_TYPES = ['xlsx', 'xlsm', 'xlsb', 'xltx', 'xltm', 'xls', 'xlt', 'xml', 'xlam', 'xla', 'xlw', 'xlr'];
const PPT_TYPES = ['pptx', 'pptm', 'ppt', 'pot', 'ppsx', 'ppsm', 'pps', 'ppa'];
const PDF_TYPES = ['pdf'];
const TXT_TYPES = ['txt'];
const ZIP_TYPES = ['iso', 'tar', 'gz', 'xz', '7z', 'rar', 'tgz', 'bz2', 'zip', 'zipx'];

const AUDIO_TYPES = [
	'3gp', 'aa', 'aac', 'aax', 'act', 'aiff', 'alac', 'amr', 'ape', 'au', 'awb', 'dct', 'dss',
	'dvf', 'flac', 'gsm', 'iklax', 'ivs', 'm4a', 'm4b', 'm4p', 'mmf', 'mp3', 'mpc', 'msv',
	'nmf', 'nsf', 'ogg', 'oga', 'mogg', 'opus', 'ra', 'rm', 'raw', 'sln', 'tta', 'voc',
	'vox', 'wav', 'wma', 'wv', '8svx', 'cda',
];
const VIDEO_TYPES = [
	'webm', 'mkv', 'flv', 'vob', 'ogv', 'drc', 'mng', 'avi', 'MTS', 'M2TS', 'TS', 'mov', 'qt',
	'wmv', 'yuv', 'rm', 'rmvb', 'asf', 'amv', 'mp4', 'm4p', 'm4v', 'mpg', 'mp2', 'mpeg',
	'mpe', 'mpv', 'mpg', 'm2v', 'm4v', 'svi', '3g2', 'mxf', 'roq', 'nsv', 'f4v ', 'f4p ',
	'f4a ', 'f4b',
];

const getFileIcon = (name: string): string => {
	if (!name) return IconUnknown;
	const suffix = name.split('.').pop()?.toLowerCase();
	if (!suffix) return IconUnknown;

	if (IMAGE_TYPES.includes(suffix)) return IconImage;
	if (WORD_TYPES.includes(suffix)) return IconWord;
	if (EXCEL_TYPES.includes(suffix)) return IconExcel;
	if (PPT_TYPES.includes(suffix)) return IconPpt;
	if (PDF_TYPES.includes(suffix)) return IconPdf;
	if (TXT_TYPES.includes(suffix)) return IconTxt;
	if (AUDIO_TYPES.includes(suffix)) return IconAudio;
	if (VIDEO_TYPES.includes(suffix)) return IconVideo;
	if (ZIP_TYPES.includes(suffix)) return IconZip;

	return IconUnknown;
};

export interface FileUploadProps extends Omit<UploadProps, 'fileList'> {
	files?: WFile[];
	labelText?: string;
	buttonProps?: ButtonProps;
	readOnly?: boolean;
	onDelFile?: (id: string) => void;
}

export interface FileUploadRef {
	getFiles: () => { delFiles: string[]; newFiles: UploadFile[] };
	addLocalFiles: (file: WFile) => void;		//慎用！未经本组件上传的文件才需要调用ref添加！
}

const FileUpload = forwardRef<FileUploadRef, FileUploadProps>(
	({ files, labelText, buttonProps, readOnly = false, onDelFile = () => { }, ...upProps }, ref) => {
		const { t } = useTranslation();
		const [localFileList, setLocalFileList] = useState<UploadFile[]>([]);

		const fileIdsStr = useMemo(() => JSON.stringify(files?.map(f => f.id)), [files]);

		useEffect(() => {
			if (files && files.length > 0) {
				setLocalFileList(
					files.map(f => ({
						uid: f.id,
						name: f.name,
						status: 'done' as const,
						url: downloadFileUrl(f.id),
						response: f.id,
					}))
				);
			} else {
				setLocalFileList([]);
			}
		}, [fileIdsStr]);

		useImperativeHandle(ref, () => ({
			getFiles: () => {
				const currentUids = new Set(localFileList.map(f => f.uid));
				const originalIds = new Set(files?.map(f => f.id) || []);
				const delFiles = Array.from(originalIds).filter(id => !currentUids.has(id));
				const newFiles = localFileList.filter(f => !originalIds.has(f.uid));
				return { delFiles, newFiles };
			},
			addLocalFiles: (file: WFile) => {
				setLocalFileList(prev => [...prev, {
					uid: file.id,
					name: file.name,
					status: 'done' as const,
					url: downloadFileUrl(file.id),
					response: file.id,
				}])
			}
		}));

		const beforeUpload: UploadProps['beforeUpload'] = (file) => {
			setLocalFileList(prev => {
				const newList = [
					...prev,
					{
						uid: file.uid,
						name: file.name,
						status: 'done' as const,
						originFileObj: file,
					},
				];
				const maxCount = upProps.maxCount;
				if (maxCount && maxCount > 0 && newList.length > maxCount) {
					return newList.slice(newList.length - maxCount);
				}
				return newList;
			});
			return false;
		};

		const handleDelete = (uid: string) => {
			setLocalFileList(prev => prev.filter(f => f.uid !== uid));
			onDelFile(uid);
		};

		const isImageFile = (file: UploadFile): boolean => {
			const suffix = file.name.split('.').pop()?.toLowerCase();
			return suffix ? IMAGE_TYPES.includes(suffix) : false;
		};

		const itemRender: UploadProps['itemRender'] = (originNode, file) => {
			if (isImageFile(file)) {
				return originNode;
			}
			return (
				<Flex gap={8} align='center' justify='space-between'
					style={{ marginBottom: 6, padding: 4, borderRadius: 8, border: "1px solid #d9d9d9" }}
				>
					<Flex gap={8} align='center'>
						<img src={getFileIcon(file.name)} alt="" style={{ width: 48, height: 48 }} />
						<span>{file.name}</span>
					</Flex>
					<Flex gap={8} align='center'>
						<Button type='text' icon={<DownloadOutlined />} size="small" disabled={false} onClick={() => downloadFile(file.uid)} />
						<Button type='text' icon={<DeleteOutlined />} size="small" disabled={readOnly} onClick={() => handleDelete(file.uid)} />
					</Flex>
				</Flex>
			);
		};

		return (
			<Fragment>
				<Upload
					fileList={localFileList}
					beforeUpload={beforeUpload}
					listType="picture"
					itemRender={itemRender}
					{...upProps}
					disabled={readOnly}
				>
					<Button icon={<UploadOutlined />} {...buttonProps} style={{ marginBottom: 6, ...buttonProps?.style }} disabled={readOnly}>{labelText || t('components.upload')}</Button>
				</Upload>
				{localFileList.length === 0 && (
					<Flex justify='center' ><Empty description={t('common.noFiles')} /></Flex>
				)}
			</Fragment>
		);
	}
);

export default FileUpload;